import { type CMSProductShelfData } from '@/features/cms/components/CMS/types'
import { useRenderShelfProducts } from '@/features/shop/components/Shelf/hooks/useRenderShelfProducts'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import styled from 'styled-components'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Box } from '@shipt/design-system-layouts'
import { IconButton } from '@shipt/design-system-buttons'
import { CloseIcon } from '@shipt/design-system-icons'
import { CarouselSection } from '@shipt/design-system-carousel'
import { useEffect, useMemo } from 'react'
import { screenSizes } from '@shipt/design-system-themes'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { trackSponsoredContentLoaded } from '@/features/shared/analytics/promotions'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = {
  shelf: CMSProductShelfData
  index: number
  handleClose: () => void
}

export const ComplementaryProductShelf = ({ shelf, index, handleClose }: Props) => {
  const { data, name, id, content_type_id, analytic_slug } = shelf
  const adPlacementId = shelf.metadata?.read_operation_info?.ad_placement_id
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()

  const normalizedProducts = useMemo(() => {
    return data.items?.map((item) => normalizeProduct(item)) ?? []
  }, [data.items])

  const shelfProducts = useRenderShelfProducts({
    products: normalizedProducts,
    shelfIndex: index,
    shelfName: name,
    hasBackground: true,
    hideSecondaryDetails: true,
    hideRatings: data.hide_ratings,
    trackingProps: { analyticSlug: analytic_slug, adPlacementId },
    linkToUniversalPDP: false,
  })

  useEffect(() => {
    shelf.metadata?.read_operation_info?.aggregate_onLoadBeacons?.forEach((beaconUrl) => {
      trackSponsoredContentLoaded({
        beaconUrl,
        featuredTransactionID: shelf.metadata?.read_operation_info?.featuredTransactionID ?? '',
        adPlacementId: shelf.metadata?.read_operation_info?.ad_placement_id,
      })
    })
  }, [shelf])

  const handleButtonClick = () => {
    handleClose()
    trackCMSElementClicked({
      content: 'close shelf',
      type: 'button',
      shelf_index: index,
      shelf_name: analytic_slug ?? name,
    })
  }

  const trackShelfViewed = () => {
    trackCMSElementViewed({
      shelf_index: index,
      shelf_name: analytic_slug ?? name,
    })
  }

  return (
    <ScrollIntoView onInView={trackShelfViewed}>
      <ShelfWrapper padding={['lg', 0]} {...getCMSWrapperProps({ id, content_type_id })}>
        <CarouselSection hasInlineControls>
          <Header
            title={data.heading ?? ''}
            trailing={
              <IconButton
                icon={CloseIcon}
                size="sm"
                variant="ghost"
                aria-label="close complementary products shelf"
                onClick={handleButtonClick}
              />
            }
          />
          <CarouselSection.Carousel
            layoutConfig={{
              [0]: {
                // @ts-expect-error is valid attribute accepted by swiper but not currently typed as such in Astro.
                slidesPerView: 'auto',
                slidesPerGroup: 1,
                slidesPerGroupAuto: true,
              },
            }}
          >
            {shelfProducts}
          </CarouselSection.Carousel>
        </CarouselSection>
      </ShelfWrapper>
    </ScrollIntoView>
  )
}

const ShelfWrapper = styled(Box)`
  /* this variable is used to set the gradient color behind the nav buttons inside the Carousel */
  --carousel-container-bg: ${theme.color.background.base.neutral.subtle_100};
  background-color: var(--carousel-container-bg);

  @media ${screenSizes.tablet} {
    border-radius: 0.5rem;
  }

  .swiper-slide {
    width: auto;
  }
`

const Header = styled(CarouselSection.Header)`
  @media ${screenSizes.tablet} {
    padding: 0 1rem;
  }
`
